import { Environment } from '@provider-directory/services/getEnvironmentVariables';
import type { EnvScopedUrl } from '@provider-directory/types/runtime-config.contracts';

/**
 * Config key per deployment environment. The `Environment` enum values are the
 * config keys verbatim, but the mapping is written out rather than indexing with
 * the enum so that LOCAL — which deliberately has no key — is a compile-time
 * absence instead of a silent `undefined` lookup.
 */
const CONFIG_KEY_BY_ENVIRONMENT: Partial<
  Record<Environment, Exclude<keyof EnvScopedUrl, 'default'>>
> = {
  [Environment.DEV]: 'development',
  [Environment.QA]: 'qa',
  [Environment.STAGING]: 'staging',
  [Environment.PROD]: 'production',
};

/**
 * Resolves an environment-scoped URL for the running environment.
 *
 * Backend stores the whole map (so promotion between environments copies
 * documents verbatim) and the frontend picks its own entry, exactly as it did
 * when these maps lived in `CLIENT_ORG_CONFIGS`.
 *
 * Falls back to `default` for any non-production environment without an
 * explicit entry — including LOCAL, which has no key of its own. PROD never
 * falls back: `production` is required by the schema precisely so a missing
 * entry cannot quietly serve a test URL to patients.
 *
 * Returns `undefined` only when the map has neither the environment's key nor a
 * `default`; the schema's refine makes that unreachable for a valid config, and
 * callers treat it as "not configured".
 */
export const resolveEnvScopedUrl = (
  urls: EnvScopedUrl,
  env: Environment,
): string | undefined => {
  const key = CONFIG_KEY_BY_ENVIRONMENT[env];
  if (key === undefined) {
    return urls.default;
  }
  return urls[key] ?? urls.default;
};
